import { escapeHtml } from "./formatters.js";
import { renderDataAttributes } from "./trustedRenderer.js";

export function renderExerciseSearchResults(items, options = {}) {
  const safeItems = Array.isArray(items) ? items.slice(0, options.limit || 8) : [];
  const emptyMessage = String(options.emptyMessage || "검색 결과가 없습니다.");
  const emptyClassName = String(options.emptyClassName || "").trim();
  const action = String(options.action || "").trim();
  const buttonLabel = String(options.buttonLabel || "선택");
  const extraAttributes = typeof options.extraAttributes === "function"
    ? options.extraAttributes
    : () => ({});

  if (safeItems.length <= 0) {
    const className = emptyClassName ? `meta ${escapeHtml(emptyClassName)}` : "meta";
    return `<p class="${className}">${escapeHtml(emptyMessage)}</p>`;
  }

  return safeItems
    .map((item, index) => {
      const attributes = {
        action,
        "exercise-id": item?.id,
        "exercise-name": item?.nameKo || item?.nameEn || "",
        ...extraAttributes(item, index),
      };

      return `
        <div class="program-search-result">
          <div class="program-search-result-content">
            <p class="program-search-result-name">${escapeHtml(item?.nameKo || item?.nameEn || "운동")}</p>
            <p class="small">
              ${escapeHtml(item?.primaryMuscle || "-")}
              ${item?.abbr ? ` · 약칭 ${escapeHtml(item.abbr)}` : ""}
              ${(item?.aliases || []).length > 0 ? ` · ${escapeHtml((item.aliases || []).slice(0, 4).join(", "))}` : ""}
            </p>
          </div>
          <button class="secondary"${renderDataAttributes(attributes)}>
            ${escapeHtml(buttonLabel)}
          </button>
        </div>
      `;
    })
    .join("");
}

export function mapExerciseSearchItems(items) {
  return (items || []).slice(0, 8).map((entry) => ({
    id: entry.id,
    nameKo: entry.nameKo,
    nameEn: entry.nameEn,
    primaryMuscle: entry.primaryMuscle,
    abbr: entry.abbr || "",
    aliases: entry.aliases || [],
  }));
}

function normalizeExerciseSearchItems(items, mapItems) {
  const safeItems = Array.isArray(items) ? items : [];
  if (typeof mapItems === "function") {
    return mapItems(safeItems);
  }
  return mapExerciseSearchItems(safeItems);
}

async function requestExerciseSearchResult(getApi, query, trackLabel) {
  const api = typeof getApi === "function" ? getApi() : null;
  if (!api || typeof api.searchExercises !== "function") {
    return { items: [] };
  }
  if (typeof api.trackJourney === "function") {
    return api.trackJourney(trackLabel, async () => api.searchExercises(query));
  }
  return api.searchExercises(query);
}

export function createExerciseSearchSession(options = {}) {
  const debounceMs = Number.isFinite(options.debounceMs) && options.debounceMs >= 0 ? options.debounceMs : 180;
  const mapItems = typeof options.mapItems === "function" ? options.mapItems : mapExerciseSearchItems;
  const trackLabel = String(options.trackLabel || "exercise search").trim() || "exercise search";
  const getApi = typeof options.getApi === "function" ? options.getApi : () => options.api;
  let timer = null;
  let requestSeq = 0;

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  async function runSearch(query, handlers = {}, currentSeq) {
    const result = await requestExerciseSearchResult(getApi, query, trackLabel);
    if (currentSeq !== requestSeq || handlers.isStale?.(query, result) === true) {
      return [];
    }

    const items = normalizeExerciseSearchItems(result?.items, mapItems);
    handlers.onResults?.(items, query, result);
    return items;
  }

  function nextRequestSeq() {
    requestSeq += 1;
    return requestSeq;
  }

  async function search(query, handlers = {}) {
    clearTimer();
    const safeQuery = String(query || "").trim();
    const currentSeq = nextRequestSeq();
    if (!safeQuery) {
      handlers.onEmpty?.();
      return [];
    }
    return runSearch(safeQuery, handlers, currentSeq);
  }

  function schedule(query, handlers = {}) {
    clearTimer();
    const safeQuery = String(query || "").trim();
    const currentSeq = nextRequestSeq();
    if (!safeQuery) {
      handlers.onEmpty?.();
      return null;
    }

    timer = setTimeout(() => {
      timer = null;
      runSearch(safeQuery, handlers, currentSeq).catch((error) => {
        if (currentSeq !== requestSeq) {
          return;
        }
        handlers.onError?.(error, safeQuery);
      });
    }, debounceMs);
    return timer;
  }

  function invalidate() {
    clearTimer();
    nextRequestSeq();
  }

  return {
    clear: clearTimer,
    invalidate,
    schedule,
    search,
  };
}

export function createExerciseSearchSessionMap(options = {}) {
  const sessions = new Map();

  function getSession(key = "default") {
    const safeKey = String(key || "default");
    if (!sessions.has(safeKey)) {
      sessions.set(safeKey, createExerciseSearchSession(options));
    }
    return sessions.get(safeKey);
  }

  function clear(key) {
    if (key === undefined) {
      sessions.forEach((session) => {
        session.clear();
      });
      return;
    }
    getSession(key).clear();
  }

  function invalidate(key) {
    if (key === undefined) {
      sessions.forEach((session) => {
        session.invalidate();
      });
      return;
    }
    getSession(key).invalidate();
  }

  return {
    clear,
    getSession,
    invalidate,
    schedule(key, query, handlers = {}) {
      return getSession(key).schedule(query, handlers);
    },
    search(key, query, handlers = {}) {
      return getSession(key).search(query, handlers);
    },
  };
}

export async function resolveExerciseIdFromQuery(api, query, selectedExerciseId = "") {
  const safeSelectedId = String(selectedExerciseId || "").trim();
  const safeQuery = String(query || "").trim();

  if (safeSelectedId) {
    return safeSelectedId;
  }

  const cachedMatch = typeof api?.findCachedExerciseSearchMatch === "function"
    ? api.findCachedExerciseSearchMatch(safeQuery)
    : null;
  if (cachedMatch?.id) {
    return cachedMatch.id;
  }

  const searchResult = await api.searchExercises(safeQuery);
  const top = (searchResult.items || [])[0];
  if (!top?.id) {
    throw new Error("입력한 검색어와 일치하는 운동을 찾을 수 없습니다");
  }

  return top.id;
}
